import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { StatusBadge } from '../components/StatusBadge';
import { type Me, useSession } from '../hooks/useSession';
import { useListPesquisas } from '../hooks/usePesquisas';
import { apiFetch } from '../lib/api';

const fmtData = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

export const DashboardPage = () => {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useSession();
  const session = me as Me; // ProtectedRoute garante non-null
  const { data, isPending, error } = useListPesquisas({ limit: 50 });

  const logout = useMutation({
    mutationFn: async () => apiFetch('/auth/sign-out', { method: 'POST' }),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      nav('/login');
    },
  });

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-navy-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="text-right text-sm">
              <p className="text-navy-900 font-medium">{session.email}</p>
              <p className="text-navy-500 text-xs">{session.cargo}</p>
            </div>
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="btn-ghost text-sm"
              disabled={logout.isPending}
            >
              sair
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-display font-semibold">pesquisas de preços</h2>
            <p className="text-sm text-navy-500 mt-1">
              município id: <code className="font-mono">{session.tenant_id}</code>
            </p>
          </div>
          <Link to="/pesquisas/nova" className="btn-primary">
            + nova pesquisa
          </Link>
        </div>

        {isPending && (
          <div className="card text-center py-12 text-navy-500 text-sm">carregando...</div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
            erro ao carregar: {(error as Error).message}
          </div>
        )}

        {data && data.data.length === 0 && (
          <div className="card text-center py-12">
            <div className="text-4xl mb-3">📂</div>
            <h3 className="font-display font-semibold text-lg mb-1">nenhuma pesquisa ainda</h3>
            <p className="text-navy-500 text-sm mb-4 max-w-md mx-auto">
              comece criando sua primeira pesquisa de preços — defina o objeto e os itens.
            </p>
            <Link to="/pesquisas/nova" className="btn-primary text-sm">
              criar pesquisa
            </Link>
          </div>
        )}

        {data && data.data.length > 0 && (
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-navy-50 text-navy-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">objeto</th>
                  <th className="text-left px-4 py-3 font-medium">status</th>
                  <th className="text-right px-4 py-3 font-medium">itens</th>
                  <th className="text-right px-4 py-3 font-medium">criada em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {data.data.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-navy-50 cursor-pointer"
                    onClick={() => nav(`/pesquisas/${p.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy-900">{p.objeto}</div>
                      <div className="text-xs text-navy-400 font-mono mt-0.5">
                        {p.id.slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.qtd_itens}</td>
                    <td className="px-4 py-3 text-right text-navy-500">{fmtData(p.criada_em)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.next_cursor && (
              <div className="border-t border-navy-100 p-3 text-center text-xs text-navy-500">
                {data.data.length} pesquisas exibidas — paginacao via cursor (TODO botao
                'carregar mais')
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-navy-100 py-4">
        <p className="text-center text-xs text-navy-400">by lions intelligence</p>
      </footer>
    </div>
  );
};
